/**
 * Generated by orval v6.16.0 🍺
 * Do not edit manually.
 * User Experiences
 * API for tracking First Time User Experiences (FTUEs).
 * OpenAPI spec version: 1.0.0
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import type {
  UseQueryOptions,
  UseMutationOptions,
  QueryFunction,
  MutationFunction,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import type {
  SuccessResponseFtueStatusList,
  ForbiddenResponse,
  UserNotFoundResponse,
  ServerErrorResponse,
  SuccessResponseBoolean,
  InvalidExperienceResponse,
} from './models';
import { customInstance } from '../core/customInstance';
import type { ErrorType } from '../core/customInstance';

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Returns all FTUE records that have been created for the specified user.
A record exists once the user has been presented with the FTUE at least once.

 * @summary List FTUE statuses for a user
 */
export const listUserExperiences = (
  userId: number,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<SuccessResponseFtueStatusList>(
    { url: `/user-experiences/${userId}/`, method: 'get', signal },
    options,
  );
};

export const getListUserExperiencesQueryKey = (userId: number) =>
  [`/user-experiences/${userId}/`] as const;

export const getListUserExperiencesQueryOptions = <
  TData = Awaited<ReturnType<typeof listUserExperiences>>,
  TError = ErrorType<ForbiddenResponse | UserNotFoundResponse | ServerErrorResponse>,
>(
  userId: number,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listUserExperiences>>, TError, TData>;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof listUserExperiences>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListUserExperiencesQueryKey(userId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listUserExperiences>>> = ({ signal }) =>
    listUserExperiences(userId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!userId, ...queryOptions };
};

export type ListUserExperiencesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listUserExperiences>>
>;
export type ListUserExperiencesQueryError = ErrorType<
  ForbiddenResponse | UserNotFoundResponse | ServerErrorResponse
>;

/**
 * @summary List FTUE statuses for a user
 */
export const useListUserExperiences = <
  TData = Awaited<ReturnType<typeof listUserExperiences>>,
  TError = ErrorType<ForbiddenResponse | UserNotFoundResponse | ServerErrorResponse>,
>(
  userId: number,
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof listUserExperiences>>, TError, TData>;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getListUserExperiencesQueryOptions(userId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Returns whether the specified FTUE has been dismissed for the user.
If a record does not already exist, one is created and the response indicates that the FTUE has not been dismissed.
NOTE: As a throttle, the backend auto-dismisses the FTUE after the flag has been read 14 times.
Calling this endpoint more than 14 times will therefore mutate server state and mark the FTUE as dismissed.

 * @summary Retrieve FTUE dismissal status
 */
export const getUserExperience = (
  userId: number,
  experienceId:
    | 'ClinicalInsightsPreventCalc'
    | 'FaxLetter'
    | 'GlobalPatientSearchPopup'
    | 'InboxAnnotateAndProviderLetter'
    | 'InboxPreviewPatientLetter'
    | 'NoteAssistFeedbackPopover'
    | 'PLR4Everyone'
    | 'Payments2ElectricBoogalooPopup'
    | 'PostDateLetter'
    | 'ReportPreviewBanner'
    | 'SendTelehealthInstructions'
    | 'TelehealthInRemindersNotice',
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal,
) => {
  return customInstance<SuccessResponseBoolean>(
    { url: `/user-experiences/${userId}/experience/${experienceId}/`, method: 'get', signal },
    options,
  );
};

export const getGetUserExperienceQueryKey = (
  userId: number,
  experienceId:
    | 'ClinicalInsightsPreventCalc'
    | 'FaxLetter'
    | 'GlobalPatientSearchPopup'
    | 'InboxAnnotateAndProviderLetter'
    | 'InboxPreviewPatientLetter'
    | 'NoteAssistFeedbackPopover'
    | 'PLR4Everyone'
    | 'Payments2ElectricBoogalooPopup'
    | 'PostDateLetter'
    | 'ReportPreviewBanner'
    | 'SendTelehealthInstructions'
    | 'TelehealthInRemindersNotice',
) => [`/user-experiences/${userId}/experience/${experienceId}/`] as const;

export const getGetUserExperienceQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserExperience>>,
  TError = ErrorType<
    ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
  >,
>(
  userId: number,
  experienceId:
    | 'ClinicalInsightsPreventCalc'
    | 'FaxLetter'
    | 'GlobalPatientSearchPopup'
    | 'InboxAnnotateAndProviderLetter'
    | 'InboxPreviewPatientLetter'
    | 'NoteAssistFeedbackPopover'
    | 'PLR4Everyone'
    | 'Payments2ElectricBoogalooPopup'
    | 'PostDateLetter'
    | 'ReportPreviewBanner'
    | 'SendTelehealthInstructions'
    | 'TelehealthInRemindersNotice',
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getUserExperience>>, TError, TData>;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getUserExperience>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetUserExperienceQueryKey(userId, experienceId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserExperience>>> = ({ signal }) =>
    getUserExperience(userId, experienceId, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(userId && experienceId), ...queryOptions };
};

export type GetUserExperienceQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserExperience>>
>;
export type GetUserExperienceQueryError = ErrorType<
  ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
>;

/**
 * @summary Retrieve FTUE dismissal status
 */
export const useGetUserExperience = <
  TData = Awaited<ReturnType<typeof getUserExperience>>,
  TError = ErrorType<
    ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
  >,
>(
  userId: number,
  experienceId:
    | 'ClinicalInsightsPreventCalc'
    | 'FaxLetter'
    | 'GlobalPatientSearchPopup'
    | 'InboxAnnotateAndProviderLetter'
    | 'InboxPreviewPatientLetter'
    | 'NoteAssistFeedbackPopover'
    | 'PLR4Everyone'
    | 'Payments2ElectricBoogalooPopup'
    | 'PostDateLetter'
    | 'ReportPreviewBanner'
    | 'SendTelehealthInstructions'
    | 'TelehealthInRemindersNotice',
  options?: {
    query?: UseQueryOptions<Awaited<ReturnType<typeof getUserExperience>>, TError, TData>;
    request?: SecondParameter<typeof customInstance>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUserExperienceQueryOptions(userId, experienceId, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Marks the FTUE as dismissed for the specified user.
 * @summary Dismiss FTUE for a user
 */
export const dismissUserExperience = (
  userId: number,
  experienceId:
    | 'ClinicalInsightsPreventCalc'
    | 'FaxLetter'
    | 'GlobalPatientSearchPopup'
    | 'InboxAnnotateAndProviderLetter'
    | 'InboxPreviewPatientLetter'
    | 'NoteAssistFeedbackPopover'
    | 'PLR4Everyone'
    | 'Payments2ElectricBoogalooPopup'
    | 'PostDateLetter'
    | 'ReportPreviewBanner'
    | 'SendTelehealthInstructions'
    | 'TelehealthInRemindersNotice',
  options?: SecondParameter<typeof customInstance>,
) => {
  return customInstance<SuccessResponseBoolean>(
    { url: `/user-experiences/${userId}/experience/${experienceId}/`, method: 'post' },
    options,
  );
};

export const getDismissUserExperienceMutationOptions = <
  TError = ErrorType<
    ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissUserExperience>>,
    TError,
    {
      userId: number;
      experienceId:
        | 'ClinicalInsightsPreventCalc'
        | 'FaxLetter'
        | 'GlobalPatientSearchPopup'
        | 'InboxAnnotateAndProviderLetter'
        | 'InboxPreviewPatientLetter'
        | 'NoteAssistFeedbackPopover'
        | 'PLR4Everyone'
        | 'Payments2ElectricBoogalooPopup'
        | 'PostDateLetter'
        | 'ReportPreviewBanner'
        | 'SendTelehealthInstructions'
        | 'TelehealthInRemindersNotice';
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof dismissUserExperience>>,
  TError,
  {
    userId: number;
    experienceId:
      | 'ClinicalInsightsPreventCalc'
      | 'FaxLetter'
      | 'GlobalPatientSearchPopup'
      | 'InboxAnnotateAndProviderLetter'
      | 'InboxPreviewPatientLetter'
      | 'NoteAssistFeedbackPopover'
      | 'PLR4Everyone'
      | 'Payments2ElectricBoogalooPopup'
      | 'PostDateLetter'
      | 'ReportPreviewBanner'
      | 'SendTelehealthInstructions'
      | 'TelehealthInRemindersNotice';
  },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof dismissUserExperience>>,
    {
      userId: number;
      experienceId:
        | 'ClinicalInsightsPreventCalc'
        | 'FaxLetter'
        | 'GlobalPatientSearchPopup'
        | 'InboxAnnotateAndProviderLetter'
        | 'InboxPreviewPatientLetter'
        | 'NoteAssistFeedbackPopover'
        | 'PLR4Everyone'
        | 'Payments2ElectricBoogalooPopup'
        | 'PostDateLetter'
        | 'ReportPreviewBanner'
        | 'SendTelehealthInstructions'
        | 'TelehealthInRemindersNotice';
    }
  > = (props) => {
    const { userId, experienceId } = props ?? {};

    return dismissUserExperience(userId, experienceId, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DismissUserExperienceMutationResult = NonNullable<
  Awaited<ReturnType<typeof dismissUserExperience>>
>;

export type DismissUserExperienceMutationError = ErrorType<
  ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
>;

/**
 * @summary Dismiss FTUE for a user
 */
export const useDismissUserExperience = <
  TError = ErrorType<
    ForbiddenResponse | UserNotFoundResponse | InvalidExperienceResponse | ServerErrorResponse
  >,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof dismissUserExperience>>,
    TError,
    {
      userId: number;
      experienceId:
        | 'ClinicalInsightsPreventCalc'
        | 'FaxLetter'
        | 'GlobalPatientSearchPopup'
        | 'InboxAnnotateAndProviderLetter'
        | 'InboxPreviewPatientLetter'
        | 'NoteAssistFeedbackPopover'
        | 'PLR4Everyone'
        | 'Payments2ElectricBoogalooPopup'
        | 'PostDateLetter'
        | 'ReportPreviewBanner'
        | 'SendTelehealthInstructions'
        | 'TelehealthInRemindersNotice';
    },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}) => {
  const mutationOptions = getDismissUserExperienceMutationOptions(options);

  return useMutation(mutationOptions);
};
